import { produce } from 'immer'
import { create } from 'zustand'
import { BehaviorSubject } from "rxjs";
import { FormProjetoHibridoLoadingsType, FormProjetoHibridoModalsType, FormProjetoHibridoType, FormProjetoHibridoValidationsType, SolucaoCalculadaHibrido } from './hibrido.types';
import { Bateria, EstruturaListagemResponse, ListagemItem, ModuloPorRegiaoListagemResponse, OptionWithEstrutura } from 'src/components/projetos/types';
import { DEFAULT_PROJETO_HIBRIDO_VALIDATIONS, DEFAULT_PROJETO_HIBRIDO, DEFAULT_PROJETO_HIBRIDO_MODALS, DEFAULT_PROJETO_HIBRIDO_LOADINGS } from './hibrido.constants';
import { INVERTER_MAX_POWER_ATTR, MODULO_POTENCIA_MAXIMA_ATTR, MODULO_POTENCIA_MINIMA_ATTR, MODULO_POWER_ATTR, POSICAO_MODULO } from 'src/components/projetos/constants';
import { ID_INVERSOR_HIBRIDO } from 'src/components/componentes/types';
import { showEstruturasInvalidasAlert } from 'src/components/projetos/utils/showEstruturasInvalidasAlert';
import { elSemEstrutura } from 'src/components/projetos/dimensionados_hibrido/containers/modalEditarEstruturas';
import { notifierStore } from 'src/framework/store/NotifierStore';

interface ProjetosHibridoStoreState {
  data: FormProjetoHibridoType
  validations: FormProjetoHibridoValidationsType
  modals: FormProjetoHibridoModalsType
  loadings: FormProjetoHibridoLoadingsType
}

type EstruturasInput = {
  ativos: EstruturaListagemResponse[];
  todos: EstruturaListagemResponse[];
  retrato: EstruturaListagemResponse[];
  paisagem: EstruturaListagemResponse[];
}

type CheckEstruturaSelecionadaOptions = {
  triggerValidation?: boolean
}

interface ProjetosHibridoStoreActions {
  changeValue: (newValues: Partial<FormProjetoHibridoType>) => void
  reset: () => void
  resetFileiras: () => void
  resetCargasPrioritarias: () => void;
  addModulos: (modulos: ModuloPorRegiaoListagemResponse[], moduloSelecionado?: { id: number, quantidade: number }) => void
  addBaterias: (baterias: Bateria[]) => void
  addEstruturas: (estruturas: EstruturasInput) => void
  checkEstruturaSelecionada: (options?: CheckEstruturaSelecionadaOptions) => void
  atualizaOrCriaFileira: (fileira: Partial<FormProjetoHibridoType["fileiras"][0]>, index?: number) => void;
  changeValidationsValue: (newValues: Partial<FormProjetoHibridoValidationsType>) => void
  changeModalsValue: (newValues: Partial<FormProjetoHibridoModalsType>) => void
  addSolucaoCalculada: (data: SolucaoCalculadaHibrido) => void
  syncFileiras: (qtdModulo?: number, estruturaSelecionada?: any, alterouQtdModulos?: boolean, exibirAlertaFiname?: boolean) => void
  changeLoadingState: (loading: keyof FormProjetoHibridoLoadingsType, status: boolean) => void
}

interface ProjetosHibridoStore {
  state: ProjetosHibridoStoreState
  actions: ProjetosHibridoStoreActions
}

export const HIBRIDO_ERROR_MESSAGES = {
  FILEIRAS_INVALIDAS: 'Atenção! Configuração das fileiras incorreta. Por favor, corrija as disposições conforme a quantidade de módulos sugerida/escolhida.',
}

const INITIAL_STATE: ProjetosHibridoStoreState = {
  data: DEFAULT_PROJETO_HIBRIDO,
  validations: DEFAULT_PROJETO_HIBRIDO_VALIDATIONS,
  modals: DEFAULT_PROJETO_HIBRIDO_MODALS,
  loadings: DEFAULT_PROJETO_HIBRIDO_LOADINGS
}

export const oldEstruturasHibrido$ = new BehaviorSubject<ListagemItem[]>([]);
export const acessouEditarEstruturasHibrido$ = new BehaviorSubject<boolean>(false);

export const useProjetosHibridoStore = create<ProjetosHibridoStore>()(
  (set, get) => {
    const setState = (fn: any) => set(produce<ProjetosHibridoStore>(fn))

    return {
      state: INITIAL_STATE,
      actions: {
        changeLoadingState: (loading: keyof FormProjetoHibridoLoadingsType, status: boolean) => {
          setState(({ state }: ProjetosHibridoStore) => {
            Object.assign<typeof state["loadings"], Partial<typeof state["loadings"]>>(state.loadings, { [loading]: status })
          })
        },
        resetCargasPrioritarias: () => {
          setState(({ state }: ProjetosHibridoStore) => {
            const cargasPrioritarias = state.data.cargas_prioritarias

            Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, {
              total_carga_equipamentos_kw: 0,
              total_carga_equipamentos_w: 0,
              cargas_prioritarias: cargasPrioritarias.length ? cargasPrioritarias.map(carga => ({ ...carga, "@remove": true })) : [],
            })
          })
        },
        atualizaOrCriaFileira: (fileira: Partial<FormProjetoHibridoType["fileiras"][0]>, index?: number) => {
          setState(({ state }: ProjetosHibridoStore) => {
            const fileiras = state.data.fileiras.length ? [...state.data.fileiras] : [];

            const posicaoModulo = fileira.posicao_modulo?.value || fileiras[index || 0]?.posicao_modulo?.value;
            const estruturas = posicaoModulo === 'retrato' ? state.data.estruturas_retrato : state.data.estruturas_paisagem;
            const idEstruturaSelecionada = fileira.tipo_estrutura?.value ?? fileiras[index || 0].tipo_estrutura?.value; 
            let estruturaSelecionada = estruturas.find(item => item.id === idEstruturaSelecionada);

            if (idEstruturaSelecionada === 0) {
              estruturaSelecionada = elSemEstrutura
            }

            if (index !== undefined) {
              fileiras[index] = {
                ...fileiras[index],
                ...fileira,
                tipo_estrutura: estruturaSelecionada ? {
                  ...estruturaSelecionada,
                  value: estruturaSelecionada.id,
                  text: estruturaSelecionada.nome,
                  image: estruturaSelecionada.url_imagem,
                } : null
              };
            } else {
              if (fileiras.length) {
                fileiras[0] = {
                  ...fileiras[0],
                  ...fileira,
                  tipo_estrutura: estruturaSelecionada ? {
                  ...estruturaSelecionada,
                  value: estruturaSelecionada.id,
                  text: estruturaSelecionada.nome,
                  image: estruturaSelecionada.url_imagem,
                } : null
                };
              } else {
                fileiras.push({
                  fileiras: 1,
                  posicao_modulo: null,
                  quantidade_modulos: 0,
                  tipo_estrutura: null,
                  ...fileira,
                });
              }
            }

            Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { fileiras })
          })
        },
        checkEstruturaSelecionada: (options) => {
          setState(({ state }: ProjetosHibridoStore) => {
            const { triggerValidation = true } = options || {}
            
            Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { alterou_modulo: false })

            const primeiraFileira = state.data.fileiras?.[0] || null
            const estruturaSelecionada = primeiraFileira?.tipo_estrutura;
            const posicaoModulo = primeiraFileira?.posicao_modulo;

            if (estruturaSelecionada && estruturaSelecionada?.value && posicaoModulo) {
              const estruturas = posicaoModulo?.value === 'retrato' ? state.data.estruturas_retrato : state.data.estruturas_paisagem;
              const estruturaEncontrada = estruturas.find(estrutura => estrutura.id === estruturaSelecionada.value)

              if (!estruturaEncontrada) {
                Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { fileiras: primeiraFileira ? [{ ...primeiraFileira, tipo_estrutura: null }] : [] })
                if (triggerValidation) {
                  Object.assign<typeof state["validations"], Partial<typeof state["validations"]>>(state.validations, { estrutura: { helper: "Estrutura anterior removida: não permitido para esta UF", status: false } })
                }
              }
            }
          })
        },
        addEstruturas: (estruturas: EstruturasInput) => {
          setState(({ state }: ProjetosHibridoStore) => {
            Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { estruturas_ativas: estruturas.ativos, estruturas_retrato: estruturas.retrato, estruturas_paisagem: estruturas.paisagem, todas_estruturas: estruturas.todos })
          })

          get().actions.checkEstruturaSelecionada({
            triggerValidation: !get().state.data.alterou_modulo
          });
        },
        addBaterias: (baterias: Bateria[]) => {
          setState(({ state }: ProjetosHibridoStore) => {
            const bateriaSelecionada = state.data.tipo_bateria;

            if (bateriaSelecionada && bateriaSelecionada?.id) {
              const bateriaEncontrada = baterias.find(bateria => bateria.id === bateriaSelecionada.id)

              if (!bateriaEncontrada) {
                Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { tipo_bateria: null })
              }
            }

            Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { baterias })
          })
        },
        addModulos: (modulos: ModuloPorRegiaoListagemResponse[], moduloSelecionado?: { id: number, quantidade: number }) => {
          setState(({ state }: ProjetosHibridoStore) => {
            const newModulos = modulos.map(modulo => {
              const potencia = modulo.atributos.find(item => item.atributo === MODULO_POWER_ATTR)
              const potenciaMaxima = modulo.atributos.find(item => item.atributo === MODULO_POTENCIA_MAXIMA_ATTR)
              const potenciaMinima = modulo.atributos.find(item => item.atributo === MODULO_POTENCIA_MINIMA_ATTR)

              return {
                potenciaMaxima: Number(potenciaMaxima?.valor || 0),
                potenciaMinima: Number(potenciaMinima?.valor || 0),
                componente_id: modulo.id,
                inversoresTipos: [],
                categoria_componentes_id: modulo.categoria_componentes_id,
                potencia: Number(potencia?.valor || 0),
                faturamento: modulo.data_previsao_faturamento ? new Date(modulo.data_previsao_faturamento).toISOString() : undefined,
                nome: modulo.nome,
                descricao: modulo.descricao,
                quantidade: 0,
                selecionado: false,
                categoria: modulo.categoria_componentes_nome,
                altura: modulo.altura,
                largura: modulo.largura,
                peso: modulo.peso,
                valor: 0, // Calculado no backend
                comprimento: modulo.comprimento,
                valor_unitario: 0,
                regioes_parametros: modulo?.regioes_parametros,
                finame: modulo?.finame,
                exibir_popup_parceiro: modulo.exibir_popup_parceiro,
                
              }
            })

            if (moduloSelecionado) {
              const selectedModulo = newModulos.find(modulo => modulo.componente_id === moduloSelecionado.id)
              if (selectedModulo) {
                selectedModulo.quantidade = moduloSelecionado.quantidade
                Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { modulo_selecionado: selectedModulo })
              }
            }

            if (state.data.modulo_selecionado) {
              const selectedModulo = newModulos.find(modulo => modulo.componente_id === state.data.modulo_selecionado?.componente_id)
              if (!selectedModulo) {
                Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { modulo_selecionado: null })
                Object.assign<typeof state["validations"], Partial<typeof state["validations"]>>(state.validations, { modulo: { status: false, helper: "Módulo anterior removido: não permitido para esta UF" } })
              }
            }

            Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { modulos: newModulos })
          })
        },
        addSolucaoCalculada: (data: SolucaoCalculadaHibrido) => {
          setState(({ state }: ProjetosHibridoStore) => {
            const newBaterias = data.baterias.map(item => ({
              ...item,
              potencia: item.potencia || 0,
              valor: 0,
              componente_id: item.id,
            }))

            const newInversores = data.inversores.map(item => {
              let potencia = 0;

              if (!!item.potencia) {
                potencia = item.potencia;
              }

              if (!potencia && item.categoria_componentes_id === ID_INVERSOR_HIBRIDO) {
                potencia = Number(item.atributos.find(item => item.atributo === INVERTER_MAX_POWER_ATTR)?.valor || 0)
              }

              return {
                ...item,
                potencia,
                valor: 0,
                componente_id: item.id
              }
            });

            Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, { solucao_calculada: {
              baterias: newBaterias,
              inversores: newInversores,
            } })

            // if (data.modulo?.alterouQuantidade && !!state.data.modulo_selecionado) {
            //   Object.assign(state.data.modulo_selecionado, { quantidade: data.modulo.quantidade ?? state.data.modulo_selecionado.quantidade ?? 0 });
            // }
          })
        },
        changeValue: (newValues: Partial<FormProjetoHibridoType>) => {
          setState(({ state }: ProjetosHibridoStore) => {
            Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, newValues)
          })
        },
        changeModalsValue: (newValues: Partial<FormProjetoHibridoModalsType>) => {
          setState(({ state }: ProjetosHibridoStore) => {
            Object.assign<typeof state["modals"], Partial<typeof state["modals"]>>(state.modals, newValues)
          })
        },
        changeValidationsValue: (newValues: Partial<FormProjetoHibridoValidationsType>) => {
          setState(({ state }: ProjetosHibridoStore) => {
            Object.assign<typeof state["validations"], Partial<typeof state["validations"]>>(state.validations, newValues)
          })
        },
        syncFileiras: (qtdModulo?: number, estruturaSelecionada?: FormProjetoHibridoType["fileiras"][0]["tipo_estrutura"], alterouQtdModulos?: boolean, exibirAlertaFiname?: boolean) => {
          setState(({ state }: ProjetosHibridoStore) => {
            const estrutura_selecionada = state.data.fileiras?.[0]?.tipo_estrutura 
            const fileiras = state.data.fileiras || [];
            const moduloFiname = state.data.modulo_selecionado?.finame || false;

            const formatEstrutura = (): FormProjetoHibridoType["fileiras"][0]["tipo_estrutura"] => {
              if (!estrutura_selecionada) return null;
              const findGrupo = estrutura_selecionada?.grupos?.find(g => g.id === estrutura_selecionada?.estruturas_grupo_id) || estrutura_selecionada?.grupos?.[0];
              let itemImageUrl = findGrupo?.url_imagem || estrutura_selecionada?.image || "";

              return {
                image: itemImageUrl,
                text: estrutura_selecionada.text,
                value: estrutura_selecionada.value,
                estruturas_grupo_id: estrutura_selecionada.estruturas_grupo_id,
                exibir_popup_parceiro: estrutura_selecionada.exibir_popup_parceiro,
                grupos: estrutura_selecionada.grupos || []
              }
            }

            if (moduloFiname && fileiras.some(fileira => fileira.tipo_estrutura?.value === 0)) {
              const exibitAlertaFinameLocal = exibirAlertaFiname === undefined ? true : exibirAlertaFiname;

              if (!estruturaSelecionada && exibitAlertaFinameLocal) {
                showEstruturasInvalidasAlert()
              }

              Object.assign<typeof state["validations"], Partial<typeof state["validations"]>>(state.validations, { fileiras: { status: false, helper: HIBRIDO_ERROR_MESSAGES.FILEIRAS_INVALIDAS } })
              Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, {
                fileiras: state.data.fileiras.map(fileira => ({
                  ...fileira,
                  tipo_estrutura: null,
                }))
              })
              return;
            }

            if (estruturaSelecionada?.value === 0) {
              Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, {
                fileiras: [{
                  fileiras: 1,
                  quantidade_modulos: qtdModulo || state.data.modulo_selecionado?.quantidade || 0,
                  tipo_estrutura: estruturaSelecionada ?
                    { ...estruturaSelecionada, grupos: estruturaSelecionada.grupos || [], image: estruturaSelecionada.image, text: estruturaSelecionada.text, value: estruturaSelecionada.value, estruturas_grupo_id: estruturaSelecionada?.estruturas_grupo_id} :
                    (estrutura_selecionada ? formatEstrutura() : elSemEstrutura),
                  posicao_modulo: state.data.fileiras?.[0]?.posicao_modulo || POSICAO_MODULO.find(item => item.value === 'retrato') || null,
                }]
              })
              return;
            }

            if (!alterouQtdModulos) {
              return
            }
            
            const totalModulosFileiras = state.data.fileiras.reduce((acc, fileira) => acc + (Number(fileira.fileiras || 0) * Number(fileira.quantidade_modulos || 0)), 0)
            const totalModulosSelecionados = state.data.modulo_selecionado?.quantidade || 0;

            if (totalModulosFileiras === totalModulosSelecionados) {
              Object.assign<typeof state["validations"], Partial<typeof state["validations"]>>(state.validations, { fileiras: { status: true, helper: '' } })
              Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, {
                fileiras: state.data.fileiras.map(fileira => {
                  return { ...fileira, tipo_estrutura: fileira.tipo_estrutura ? fileira.tipo_estrutura : elSemEstrutura }
                })
              })

              return;
            }

            Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, {
              fileiras: [{
                fileiras: 1,
                quantidade_modulos: qtdModulo || state.data.modulo_selecionado?.quantidade || 0,
                tipo_estrutura: estruturaSelecionada ?
                  { ...estruturaSelecionada, image: estruturaSelecionada.image, text: estruturaSelecionada.text, value: estruturaSelecionada.value, estruturas_grupo_id: estruturaSelecionada?.estruturas_grupo_id} :
                  (estrutura_selecionada ? formatEstrutura() : elSemEstrutura),
                posicao_modulo: state.data.fileiras?.[0]?.posicao_modulo || POSICAO_MODULO.find(item => item.value === 'retrato') || null,
              }]
            })

            if (acessouEditarEstruturasHibrido$.getValue()) {
              notifierStore.setNotifier({
                content: "Importante: Quantidade de estruturas recalculada considerando o cenário padrão de uma (1) fileira em posição retrato. Revise a disposição dos módulos e fileiras, se necessário.",
                onClose: () => notifierStore.setOpen(false),
                timeOut: 4000,
                title: "Aviso!"
              })
              notifierStore.setOpen(true)
            }

            acessouEditarEstruturasHibrido$.next(false)

          })
        },
        reset: () => {
          setState(({ state }: ProjetosHibridoStore) => {
            Object.assign<typeof state, Partial<typeof state>>(state, INITIAL_STATE)
          })
        },
        resetFileiras: () => {
          setState(({ state }: ProjetosHibridoStore) => {
            const posicaoModulo = state.data.fileiras?.[0]?.posicao_modulo || null;
            
            Object.assign<typeof state["data"], Partial<typeof state["data"]>>(state.data, {
              fileiras: [
                {
                  fileiras: 1,
                  quantidade_modulos: 0,
                  tipo_estrutura: elSemEstrutura,
                  posicao_modulo: posicaoModulo || POSICAO_MODULO.find(item => item.value === 'retrato') || null,
                }
              ]
            })
          })
        },
      },
    }
  },
)
